import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import StoreLayout from "../components/StoreLayout";
import { useAuth } from "../context/AuthContext";

const BG = "https://images.unsplash.com/photo-1761073490980-ecd40e9bec57?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxzdHJlZXR3ZWFyJTIwZmFzaGlvbiUyMG1vZGVsJTIwbmVvbiUyMGRhcmslMjB1cmJhbnxlbnwwfHx8fDE3Nzk1MTk0Njd8MA&ixlib=rb-4.1.0&q=85";

// Common country codes for shipping — India first (home market), then top international
const COUNTRY_CODES = [
  { code: "+91", label: "🇮🇳 IN +91" },
  { code: "+1", label: "🇺🇸 US +1" },
  { code: "+44", label: "🇬🇧 UK +44" },
  { code: "+61", label: "🇦🇺 AU +61" },
  { code: "+971", label: "🇦🇪 AE +971" },
  { code: "+65", label: "🇸🇬 SG +65" },
  { code: "+49", label: "🇩🇪 DE +49" },
  { code: "+81", label: "🇯🇵 JP +81" },
  { code: "+33", label: "🇫🇷 FR +33" },
  { code: "+34", label: "🇪🇸 ES +34" },
  { code: "+39", label: "🇮🇹 IT +39" },
  { code: "+55", label: "🇧🇷 BR +55" },
  { code: "+86", label: "🇨🇳 CN +86" },
  { code: "+7", label: "🇷🇺 RU +7" },
  { code: "+27", label: "🇿🇦 ZA +27" },
];

export default function Signup() {
  const [form, setForm] = useState({
    name: "",
    country_code: "+91",
    phone: "",
    email: "",
    password: "",
  });
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/";

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const digits = form.phone.replace(/\D/g, "");
    if (digits.length < 6 || digits.length > 15) {
      toast.error("Enter a valid mobile number");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be 6+ chars");
      return;
    }
    setBusy(true);
    try {
      await register({
        name: form.name,
        country_code: form.country_code,
        phone: digits,
        email: form.email,
        password: form.password,
      });
      toast.success("WELCOME TO THE CULT");
      navigate(next);
    } catch (err) {
      toast.error("SIGNUP FAILED", { description: err?.response?.data?.detail || "Try again" });
    } finally {
      setBusy(false);
    }
  };

  return (
    <StoreLayout>
      <div className="grid md:grid-cols-2 min-h-[80vh]">
        <div className="relative hidden md:block">
          <img src={BG} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-br from-black/40 to-black/90" />
          <div className="absolute bottom-10 left-10 right-10">
            <p className="label-mono text-[#CFF048]">// JOIN THE CULT</p>
            <h2 className="hero-title text-5xl uppercase mt-2">Live<br/>Different.<br/>Stay Cult.</h2>
            <p className="text-[#A1A1AA] mt-4 max-w-sm">Sign up to checkout, track orders &amp; unlock member-only drops.</p>
          </div>
        </div>
        <div className="flex items-center justify-center p-8 md:p-16">
          <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
            <p className="label-mono text-[#71717A]">// AUTH</p>
            <h1 className="font-display font-black text-4xl md:text-5xl uppercase mt-3">Sign Up</h1>
            <p className="text-[#A1A1AA] mt-2">Become part of the cult.</p>

            <div className="mt-8 space-y-4">
              <input
                required
                value={form.name}
                onChange={(e) => setField("name", e.target.value)}
                placeholder="FULL NAME"
                className="cult-input"
                data-testid="signup-name"
              />

              {/* Mobile number — country code + phone (BEFORE email) */}
              <div className="grid grid-cols-[130px_1fr] gap-2">
                <select
                  value={form.country_code}
                  onChange={(e) => setField("country_code", e.target.value)}
                  className="cult-input"
                  data-testid="signup-country-code"
                  aria-label="Country code"
                >
                  {COUNTRY_CODES.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                <input
                  type="tel"
                  inputMode="numeric"
                  autoComplete="tel-national"
                  required
                  value={form.phone}
                  onChange={(e) => setField("phone", e.target.value.replace(/\D/g, "").slice(0, 15))}
                  placeholder="MOBILE NUMBER"
                  className="cult-input"
                  data-testid="signup-phone"
                />
              </div>

              <input
                type="email"
                required
                value={form.email}
                onChange={(e) => setField("email", e.target.value)}
                placeholder="EMAIL ADDRESS"
                className="cult-input"
                data-testid="signup-email"
              />

              <div className="relative">
                <input
                  type={showPw ? "text" : "password"}
                  required
                  value={form.password}
                  onChange={(e) => setField("password", e.target.value)}
                  placeholder="PASSWORD (6+ CHARS)"
                  className="cult-input pr-12"
                  data-testid="signup-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#71717A] hover:text-[#CFF048] transition-colors"
                  data-testid="toggle-password"
                  aria-label={showPw ? "Hide password" : "Show password"}
                >
                  {showPw ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <button disabled={busy} type="submit" className="btn-neon w-full mt-6" data-testid="signup-submit">
              {busy ? "CREATING..." : "CREATE ACCOUNT"}
            </button>
            <p className="label-mono text-[#71717A] mt-6 text-center">
              ALREADY A MEMBER? <Link to={`/login${next !== "/" ? `?next=${next}` : ""}`} className="text-[#CFF048] hover:underline">SIGN IN</Link>
            </p>
          </form>
        </div>
      </div>
    </StoreLayout>
  );
}
